// Compact roster-cell picker. Same menu look as the shared ReusableDropdown
// (dropdownPalette), but light enough to render once per grid cell: no global
// listeners until it is opened. Behaves like the <select> it replaces — it
// reports the chosen option's value through onChange(value).
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import { dropdownPalette } from "../../../../components/ReusableDropdown";

const MENU_MAX_HEIGHT = 280;
const ROW_HEIGHT = 36;

export default function CellSelect({ value, options, onChange, theme, language, style, title, ariaLabel, className }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [highlight, setHighlight] = useState(-1);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const p = dropdownPalette(theme);
  const isRTL = language === "ar";
  const selected = options.find((o) => o.value === value) || options[0];

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const height = Math.min(MENU_MAX_HEIGHT, options.length * ROW_HEIGHT + 12);
    const width = Math.max(rect.width, 210);
    const openUp = window.innerHeight - rect.bottom < height + 8 && rect.top > window.innerHeight - rect.bottom;
    let left = isRTL ? rect.right - width : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    setCoords({ top: openUp ? rect.top - height - 6 : rect.bottom + 6, left, width });
  }, [options.length, isRTL]);

  const close = useCallback(() => {
    setOpen(false);
    setCoords(null);
    setHighlight(-1);
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      close();
    };
    const onScroll = (e) => { if (!menuRef.current?.contains(e.target)) close(); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open, place, close]);

  // Keep the keyboard-highlighted row visible.
  useEffect(() => {
    if (!open || highlight < 0 || !menuRef.current) return;
    menuRef.current.querySelector(`[data-index="${highlight}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, highlight]);

  const pick = (opt) => {
    if (!opt || opt.disabled) return;
    close();
    if (opt.value !== value) onChange(opt.value);
    triggerRef.current?.focus();
  };

  const step = (dir) => {
    const n = options.length;
    let i = highlight;
    for (let k = 0; k < n; k += 1) {
      i = (i + dir + n) % n;
      if (!options[i].disabled) break;
    }
    setHighlight(i);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
        setHighlight(Math.max(0, options.findIndex((o) => o.value === value)));
      }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); step(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); step(-1); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(options[highlight]); }
    else if (e.key === "Escape" || e.key === "Tab") close();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={className}
        style={{ ...style, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "inherit" }}
        title={title}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={onKeyDown}
      >
        {selected?.label}
      </button>

      {open && coords && createPortal(
        <div
          ref={menuRef}
          className="reusable-dropdown-portal"
          role="listbox"
          style={{
            position: "fixed", top: coords.top, left: coords.left, width: coords.width,
            maxHeight: MENU_MAX_HEIGHT, overflowY: "auto", zIndex: 99999,
            background: p.menuBg, border: `1px solid ${p.menuBorder}`, boxShadow: p.menuShadow,
            borderRadius: 12, padding: 6, boxSizing: "border-box",
            direction: isRTL ? "rtl" : "ltr", animation: "rdd-in 0.12s ease-out",
          }}
        >
          {options.map((opt, i) => {
            const isSel = opt.value === value;
            return (
              <div
                key={opt.value || "__empty__"}
                role="option"
                aria-selected={isSel}
                aria-disabled={opt.disabled || undefined}
                data-index={i}
                onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  minHeight: ROW_HEIGHT - 4, padding: "7px 10px", margin: "1px 0", borderRadius: 8,
                  display: "flex", alignItems: "center", gap: 10, fontSize: 13,
                  cursor: opt.disabled ? "not-allowed" : "pointer",
                  opacity: opt.disabled ? 0.45 : 1,
                  color: isSel ? p.primary : p.text,
                  fontWeight: isSel ? 600 : 500,
                  background: isSel ? p.primarySoft : i === highlight && !opt.disabled ? p.hover : "transparent",
                  transition: "background 0.1s ease",
                }}
              >
                <span style={{
                  width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                  background: opt.color || "transparent",
                  border: opt.color ? "none" : `1.5px dashed ${p.border}`,
                }} />
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} dir="auto">
                  {opt.label}
                </span>
                {isSel && <Check size={15} style={{ flexShrink: 0, color: p.primary }} />}
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}
